// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Target, Shield, Settings, DollarSign, Clock, AlertTriangle, ToggleLeft, ToggleRight, Save, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface PaddleSettings {
  paddle_number: number | null;
  use_paddle_for_display: boolean;
  max_bid_limit: number | null;
  require_confirmation: boolean;
  spending_alerts: boolean;
  auto_increment_enabled: boolean;
  session_timeout_minutes: number;
}

const PaddleSettingsTab: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<PaddleSettings>({
    paddle_number: null,
    use_paddle_for_display: false,
    max_bid_limit: null,
    require_confirmation: true,
    spending_alerts: true,
    auto_increment_enabled: false,
    session_timeout_minutes: 30
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadPaddleSettings();
  }, [user]);

  const loadPaddleSettings = async () => {
    try {
      setLoading(true);
      
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('paddle_number, use_paddle_for_display')
        .eq('id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setSettings(prev => ({
          ...prev,
          paddle_number: data.paddle_number,
          use_paddle_for_display: data.use_paddle_for_display || false
        }));
      }
    } catch (error) {
      console.error('Error loading paddle settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load paddle settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (!user) return;

      const { error } = await supabase
        .from('profiles')
        .update({
          use_paddle_for_display: settings.use_paddle_for_display,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      
      toast({
        title: 'Success',
        description: 'Paddle settings updated successfully'
      });
    } catch (error) {
      console.error('Error saving paddle settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save paddle settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-white/70">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="font-light">Loading paddle settings...</span>
        </div>
      </div>
    );
  }

  const hasPaddle = settings.paddle_number !== null;

  return (
    <ScrollArea className="h-full">
      <div className="p-8 space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h2 className="text-2xl font-light text-white mb-2">Paddle Settings</h2>
          <p className="text-white/70 font-light">Configure your bidding preferences and safety features</p>
        </motion.div>

        {/* Paddle Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl">
            <CardHeader>
              <CardTitle className="text-white font-light flex items-center gap-3">
                <Target className="h-5 w-5 text-emerald-400" />
                Paddle Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-white font-medium">Paddle Number</p>
                  <p className="text-white/60 text-sm font-light">Your unique bidding identifier</p>
                </div>
                <div className="text-right">
                  {hasPaddle ? (
                    <div className="flex items-center gap-2">
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-400/30 text-lg px-3 py-1">
                        #{settings.paddle_number}
                      </Badge>
                      <Badge variant="outline" className="border-green-400/30 text-green-400">
                        Assigned
                      </Badge>
                    </div>
                  ) : (
                    <Badge variant="outline" className="border-yellow-400/30 text-yellow-400">
                      Not Assigned
                    </Badge>
                  )}
                </div>
              </div>

              {hasPaddle && (
                <div className="space-y-4 border-t border-white/10 pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="use_paddle_display" className="text-white font-medium">
                        Display Paddle Number
                      </Label>
                      <p className="text-white/60 text-sm font-light">
                        Show your paddle number instead of your name during bidding
                      </p>
                    </div>
                    <Switch
                      id="use_paddle_display"
                      checked={settings.use_paddle_for_display}
                      onCheckedChange={(checked) => 
                        setSettings(prev => ({ ...prev, use_paddle_for_display: checked }))
                      }
                      className="data-[state=checked]:bg-emerald-500"
                    />
                  </div>

                  {/* Preview */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-white/70 text-sm font-light mb-2">Display Preview:</p>
                    <div className="text-white font-medium">
                      {settings.use_paddle_for_display ? (
                        <span className="text-emerald-400">Paddle #{settings.paddle_number}</span>
                      ) : (
                        <span>Your Name</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!hasPaddle && (
                <div className="bg-blue-500/10 border border-blue-400/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-blue-400 font-medium mb-1">Paddle Assignment Pending</p>
                      <p className="text-blue-400/80 text-sm font-light">
                        Your paddle number will be assigned when you register for your first auction. 
                        Contact support if you need assistance.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Safety Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          {/* Bidding Limits */}
          <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl">
            <CardHeader>
              <CardTitle className="text-white font-light flex items-center gap-3">
                <Shield className="h-5 w-5 text-blue-400" />
                Safety Limits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="max_bid_limit" className="text-white/80 font-light">
                  Maximum Bid Limit
                </Label>
                <Input
                  id="max_bid_limit"
                  type="number"
                  value={settings.max_bid_limit || ''}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    max_bid_limit: e.target.value ? parseInt(e.target.value) : null 
                  }))}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-blue-400/50"
                  placeholder="Enter maximum bid amount"
                />
                <p className="text-white/60 text-xs font-light">
                  Set a limit to prevent accidental high bids
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-white font-medium">Confirmation Required</Label>
                  <p className="text-white/60 text-sm font-light">
                    Require confirmation for all bids
                  </p>
                </div>
                <Switch
                  checked={settings.require_confirmation}
                  onCheckedChange={(checked) => 
                    setSettings(prev => ({ ...prev, require_confirmation: checked }))
                  }
                  className="data-[state=checked]:bg-blue-500"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-white font-medium">Spending Alerts</Label>
                  <p className="text-white/60 text-sm font-light">
                    Get notified when approaching limits
                  </p>
                </div>
                <Switch
                  checked={settings.spending_alerts}
                  onCheckedChange={(checked) => 
                    setSettings(prev => ({ ...prev, spending_alerts: checked }))
                  }
                  className="data-[state=checked]:bg-blue-500"
                />
              </div>
            </CardContent>
          </Card>

          {/* Bidding Preferences */}
          <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl">
            <CardHeader>
              <CardTitle className="text-white font-light flex items-center gap-3">
                <Settings className="h-5 w-5 text-teal-400" />
                Bidding Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-white font-medium">Auto-Increment</Label>
                  <p className="text-white/60 text-sm font-light">
                    Automatically suggest next bid amount
                  </p>
                </div>
                <Switch
                  checked={settings.auto_increment_enabled}
                  onCheckedChange={(checked) => 
                    setSettings(prev => ({ ...prev, auto_increment_enabled: checked }))
                  }
                  className="data-[state=checked]:bg-teal-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="session_timeout" className="text-white/80 font-light">
                  Session Timeout
                </Label>
                <Select 
                  value={settings.session_timeout_minutes.toString()} 
                  onValueChange={(value) => setSettings(prev => ({ 
                    ...prev, 
                    session_timeout_minutes: parseInt(value) 
                  }))}
                >
                  <SelectTrigger className="bg-white/5 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/20">
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-white/60 text-xs font-light">
                  Automatically log out after inactivity
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="flex justify-end"
        >
          <Button
            onClick={handleSave}
            disabled={saving}
            className={cn(
              "px-8 py-3 rounded-xl font-medium transition-all duration-300",
              saved 
                ? "bg-green-500/20 border-green-400/50 text-green-400 hover:bg-green-500/30" 
                : "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
            )}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Saved
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </motion.div>
      </div>
    </ScrollArea>
  );
};

export default PaddleSettingsTab;